import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { apiRequest } from "@/lib/queryClient";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { useLocation, useSearch } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useToast } from "@/hooks/use-toast";
import { BrandingCropDialog } from "@/components/BrandingCropDialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useBusinessSpecials } from "@/hooks/use-honey";
import { timeAgo } from "@/lib/time-ago";
import type { CapacitySource } from "@shared/schema";
import {
  Home as HomeIcon,
  Radio,
  MessageSquare,
  Star,
  User as UserIcon,
  ChevronDown,
  Plus,
  Calendar,
  Edit3,
  Trash2,
  ChevronRight,
  LogOut,
  Activity,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  PlayCircle,
  ThumbsUp,
} from "lucide-react";
import type { Venue, VenueEvent } from "@shared/schema";

type TabId = "home" | "live" | "vibes" | "promote" | "profile";
type PromoteSubTab = "promotions" | "events";

const TAG_OPTIONS = [
  "Drinks Deal",
  "Food Deal",
  "Entertainment",
  "Live Music",
  "Ladies Night",
  "DJ Night",
  "Happy Hour",
  "Brunch",
];
const DAY_OPTIONS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

interface AnalyticsData {
  views: {
    today: number;
    week: number;
    total: number;
    byDay: { date: string; count: number }[];
  };
  reports: {
    totalReports: number;
    avgCapacity: number;
    peakCapacity: number;
    vibeBreakdown: Record<string, number>;
  };
  checkIns?: {
    byDay: { date: string; count: number }[];
    range?: number;
  };
}

type CheckInsRange = 7 | 30 | 90;
const CHECKINS_RANGE_OPTIONS: { value: CheckInsRange; label: string }[] = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

interface UserMediaItem {
  id: number;
  userId: number;
  username: string;
  mediaType: "photo" | "video";
  cloudinaryUrl: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  thumbsUp: number;
  thumbsDown: number;
  createdAt: string;
}

function statusFromCapacity(pct: number): { label: string; dotClass: string; textClass: string } {
  if (pct <= 0)  return { label: "Empty",   dotClass: "bg-blue-400",     textClass: "text-blue-400" };
  if (pct < 30)  return { label: "Quiet",   dotClass: "bg-emerald-400",  textClass: "text-emerald-400" };
  if (pct < 70)  return { label: "Steady",  dotClass: "bg-primary",      textClass: "text-primary" };
  if (pct < 85)  return { label: "Busy",    dotClass: "bg-orange-400",   textClass: "text-orange-400" };
  return           { label: "Buzzin'", dotClass: "bg-red-500",      textClass: "text-red-500" };
}

function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function CapacityRing({ pct }: { pct: number }) {
  const r = 30;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.max(0, Math.min(100, pct)) / 100) * c;
  return (
    <svg width={76} height={76} viewBox="0 0 76 76" data-testid="capacity-ring">
      <circle cx="38" cy="38" r={r} stroke="hsl(var(--muted))" strokeWidth="6" fill="none" />
      <circle
        cx="38"
        cy="38"
        r={r}
        stroke="hsl(var(--primary))"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform="rotate(-90 38 38)"
      />
      <text
        x="38"
        y="42"
        textAnchor="middle"
        fontSize="15"
        fontWeight="700"
        fill="hsl(var(--primary))"
      >
        {pct}%
      </text>
    </svg>
  );
}

function CapacityScale({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="space-y-2" data-testid="capacity-scale">
      <div className="relative h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-emerald-400 via-primary to-red-500"
          style={{ width: `${clamped}%` }}
        />
        <div
          className="absolute top-1/2 w-3 h-3 rounded-full bg-white border-2 border-zinc-900 -translate-y-1/2"
          style={{ left: `calc(${clamped}% - 6px)` }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-zinc-500 px-0.5">
        <span>Empty</span>
        <span>Quiet</span>
        <span>Steady</span>
        <span>Busy</span>
        <span>Buzzin'</span>
      </div>
    </div>
  );
}

function getLastNDays(byDay: { date: string; count: number }[], n: number) {
  const map = new Map(byDay.map(v => [v.date, v.count]));
  const arr: { date: string; count: number; weekdayShort: string; weekdayIdx: number; monthDay: string }[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split("T")[0];
    arr.push({
      date: dateStr,
      count: map.get(dateStr) ?? 0,
      weekdayShort: d.toLocaleDateString("en-GB", { weekday: "short" }).slice(0, 3),
      weekdayIdx: d.getDay(),
      monthDay: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    });
  }
  return arr;
}

function getPrevNDaysTotal(byDay: { date: string; count: number }[], n: number) {
  const map = new Map(byDay.map(v => [v.date, v.count]));
  let total = 0;
  for (let i = n * 2 - 1; i >= n; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    total += map.get(d.toISOString().split("T")[0]) ?? 0;
  }
  return total;
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getWeekdayBreakdown(days: { count: number; weekdayIdx: number }[]) {
  const totals = [0, 0, 0, 0, 0, 0, 0];
  for (const d of days) totals[d.weekdayIdx] += d.count;
  // Reorder Mon-first for UK audience.
  const ordered = [1, 2, 3, 4, 5, 6, 0];
  return ordered.map(idx => ({ label: WEEKDAY_LABELS[idx], count: totals[idx] }));
}

// ---------- Home Tab ----------
type CapacityHistoryRange = 7 | 30;
const CAPACITY_HISTORY_RANGE_OPTIONS: { value: CapacityHistoryRange; label: string }[] = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
];
type CapacityHistoryApiResp = {
  dates: string[];
  venues: { venueId: number; venueName: string; data: number[] }[];
};

function CapacityHistoryCard({ venueId }: { venueId: number }) {
  const [range, setRange] = useState<CapacityHistoryRange>(7);
  const { data, isLoading } = useQuery<CapacityHistoryApiResp>({
    queryKey: ["/api/business/analytics/capacity-history", { range }],
    queryFn: async () => {
      const res = await fetch(`/api/business/analytics/capacity-history?range=${range}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load capacity history");
      return res.json();
    },
    staleTime: 0,
    refetchOnMount: "always",
  });

  const chartData = useMemo(() => {
    if (!data) return [] as { day: string; pct: number }[];
    const venue = data.venues.find(v => v.venueId === venueId);
    if (!venue) return [];
    return data.dates.map((day, i) => ({ day, pct: venue.data[i] ?? 0 }));
  }, [data, venueId]);

  const hasData = chartData.some(d => d.pct > 0);

  return (
    <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="home-capacity-history-card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Capacity History</p>
        <div
          className="inline-flex items-center bg-zinc-800/70 rounded-full p-0.5"
          role="tablist"
          aria-label="Capacity history range"
          data-testid="capacity-history-range-toggle"
        >
          {CAPACITY_HISTORY_RANGE_OPTIONS.map(opt => {
            const active = opt.value === range;
            return (
              <button
                key={opt.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setRange(opt.value)}
                data-testid={`button-capacity-history-range-${opt.value}`}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${active ? "bg-primary text-primary-foreground" : "text-zinc-400 hover:text-white"}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {isLoading ? (
        <div className="h-40 bg-zinc-800/50 rounded animate-pulse" />
      ) : hasData ? (
        <div className="h-40" data-testid="capacity-history-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="day"
                tick={{ fill: "#71717a", fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: "#27272a" }}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: "#71717a", fontSize: 10 }}
                tickLine={false}
                axisLine={{ stroke: "#27272a" }}
                tickFormatter={(v) => `${v}%`}
                width={36}
              />
              <Tooltip
                contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#a1a1aa" }}
                itemStyle={{ color: "#FED100" }}
                formatter={(value: number) => [`${value}%`, "Capacity"]}
              />
              <Line
                type="monotone"
                dataKey="pct"
                stroke="#FED100"
                strokeWidth={2}
                dot={{ r: 2.5, fill: "#FED100" }}
                activeDot={{ r: 4 }}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-xs text-zinc-500 text-center py-8" data-testid="capacity-history-empty">
          No history available yet
        </p>
      )}
    </Card>
  );
}

function HomeTab({
  venue,
  analytics,
  analyticsLoading,
  checkInsRange,
  onCheckInsRangeChange,
  onQuickAction,
  onViewLive,
}: {
  venue: Venue;
  analytics: AnalyticsData | undefined;
  analyticsLoading: boolean;
  checkInsRange: CheckInsRange;
  onCheckInsRangeChange: (r: CheckInsRange) => void;
  onQuickAction: (kind: "promo" | "event" | "profile") => void;
  onViewLive: () => void;
}) {
  const pct = venue.capacityLevel ?? 0;
  const status = statusFromCapacity(pct);
  const updated = venue.lastUpdated
    ? `Updated ${timeAgo(new Date(venue.lastUpdated as unknown as string))}`
    : "Updated just now";

  const checkInsByDay = analytics?.checkIns?.byDay ?? [];
  const days = analytics ? getLastNDays(checkInsByDay, checkInsRange) : [];
  const tonight = days[days.length - 1]?.count ?? 0;
  const periodTotal = days.reduce((s, d) => s + d.count, 0);
  const prevPeriodTotal = analytics ? getPrevNDaysTotal(checkInsByDay, checkInsRange) : 0;
  const delta = prevPeriodTotal === 0
    ? (periodTotal > 0 ? 100 : 0)
    : Math.round(((periodTotal - prevPeriodTotal) / prevPeriodTotal) * 100);
  const max = Math.max(...days.map(d => d.count), 1);
  const todayIdx = days.length - 1;
  const weekdayBreakdown = checkInsRange > 7 && analytics ? getWeekdayBreakdown(days) : [];
  const weekdayMax = Math.max(...weekdayBreakdown.map(d => d.count), 1);
  const periodLabel = checkInsRange === 7 ? "This week" : `Last ${checkInsRange} days`;
  const prevPeriodLabel = checkInsRange === 7 ? "vs last week" : `vs prev ${checkInsRange}d`;
  const cardTitle = checkInsRange === 7 ? "Vibes Reported This Week" : `Vibes Reported · Last ${checkInsRange} Days`;
  // For dense ranges, show every Nth bar label so the axis stays legible.
  const labelStride = checkInsRange === 7 ? 1 : checkInsRange === 30 ? 5 : 14;

  return (
    <div className="space-y-4" data-testid="home-tab-content">
      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="home-live-capacity-card">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Live Capacity</p>
            <p className="text-5xl font-bold text-primary leading-none" data-testid="text-home-capacity">{pct}%</p>
            <p className={`text-sm font-medium mt-2 ${status.textClass}`}>{status.label} right now</p>
          </div>
          <CapacityRing pct={pct} />
        </div>
        <CapacityScale pct={pct} />
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-800">
          <span className="text-xs text-zinc-500">{updated}</span>
          <button
            data-testid="link-view-live-details"
            onClick={onViewLive}
            className="text-xs font-semibold text-primary inline-flex items-center gap-0.5 hover:opacity-80"
          >
            View details
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </Card>

      <CapacityHistoryCard venueId={venue.id} />

      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="home-checkins-card">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{cardTitle}</p>
          <div
            className="inline-flex items-center bg-zinc-800/70 rounded-full p-0.5"
            role="tablist"
            aria-label="Check-in history range"
            data-testid="checkins-range-toggle"
          >
            {CHECKINS_RANGE_OPTIONS.map(opt => {
              const active = opt.value === checkInsRange;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onCheckInsRangeChange(opt.value)}
                  data-testid={`button-checkins-range-${opt.value}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${active ? "bg-primary text-primary-foreground" : "text-zinc-400 hover:text-white"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {analyticsLoading ? (
          <div className="h-24 bg-zinc-800/50 rounded animate-pulse" />
        ) : (
          <>
            <div className="flex items-end gap-[3px] h-24 mb-1" data-testid="chart-checkins-by-day">
              {days.map((day, i) => {
                const isToday = i === todayIdx;
                return (
                  <div
                    key={day.date}
                    className="flex-1 bg-zinc-800 rounded-sm relative overflow-hidden"
                    title={`${day.monthDay}: ${day.count}`}
                  >
                    <div
                      className={`absolute bottom-0 left-0 right-0 rounded-sm transition-all duration-500 ${isToday ? "bg-primary" : "bg-zinc-700"}`}
                      style={{ height: `${(day.count / max) * 100}%`, minHeight: day.count > 0 ? "3px" : "0px" }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex gap-[3px] mb-4">
              {days.map((day, i) => {
                const isToday = i === todayIdx;
                const showLabel = isToday || (days.length - 1 - i) % labelStride === 0;
                return (
                  <span
                    key={day.date}
                    className={`flex-1 text-center text-[9px] truncate ${isToday ? "text-primary font-semibold" : "text-zinc-500"}`}
                  >
                    {showLabel ? (checkInsRange === 7 ? day.weekdayShort : day.monthDay) : ""}
                  </span>
                );
              })}
            </div>
          </>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center" data-testid="stat-tonight">
            <p className="text-xl font-bold text-primary">{tonight.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Tonight</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center" data-testid="stat-period-total">
            <p className="text-xl font-bold text-white">{periodTotal.toLocaleString()}</p>
            <p className="text-[10px] text-zinc-500 mt-0.5">{periodLabel}</p>
          </div>
          <div className="bg-zinc-800/50 rounded-xl p-3 text-center" data-testid="stat-period-delta">
            <p className={`text-xl font-bold flex items-center justify-center gap-1 ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {delta >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {Math.abs(delta)}%
            </p>
            <p className="text-[10px] text-zinc-500 mt-0.5">{prevPeriodLabel}</p>
          </div>
        </div>
      </Card>

      {checkInsRange > 7 && !analyticsLoading && analytics && (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="home-weekday-breakdown-card">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Weekday Pattern</p>
          <div className="flex items-end gap-2 h-20 mb-2">
            {weekdayBreakdown.map(d => (
              <div key={d.label} className="flex-1 bg-zinc-800 rounded-md relative overflow-hidden" title={`${d.label}: ${d.count}`}>
                <div
                  className="absolute bottom-0 left-0 right-0 rounded-md bg-primary/80 transition-all duration-500"
                  style={{ height: `${(d.count / weekdayMax) * 100}%`, minHeight: d.count > 0 ? "3px" : "0px" }}
                />
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            {weekdayBreakdown.map(d => (
              <span key={d.label} className="flex-1 text-center text-[10px] text-zinc-500" data-testid={`weekday-label-${d.label.toLowerCase()}`}>
                {d.label}
              </span>
            ))}
          </div>
        </Card>
      )}

      <div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3 px-1">Quick Actions</p>
        <div className="grid grid-cols-3 gap-3">
          <button
            data-testid="quick-action-promo"
            onClick={() => onQuickAction("promo")}
            className="bg-primary text-primary-foreground rounded-2xl p-4 flex flex-col items-center gap-2 hover-elevate active-elevate-2"
          >
            <Plus className="w-5 h-5" />
            <span className="text-sm font-semibold">Add Promo</span>
          </button>
          <button
            data-testid="quick-action-event"
            onClick={() => onQuickAction("event")}
            className="bg-zinc-900 border border-zinc-800 text-white rounded-2xl p-4 flex flex-col items-center gap-2 hover-elevate active-elevate-2"
          >
            <Calendar className="w-5 h-5" />
            <span className="text-sm font-semibold">Add Event</span>
          </button>
          <button
            data-testid="quick-action-edit-profile"
            onClick={() => onQuickAction("profile")}
            className="bg-zinc-900 border border-zinc-800 text-white rounded-2xl p-4 flex flex-col items-center gap-2 hover-elevate active-elevate-2"
          >
            <Edit3 className="w-5 h-5" />
            <span className="text-sm font-semibold">Edit Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Live Tab ----------
function LiveTab({
  venue,
  analytics,
  analyticsLoading,
}: {
  venue: Venue;
  analytics: AnalyticsData | undefined;
  analyticsLoading: boolean;
}) {
  const pct = venue.capacityLevel ?? 0;
  const status = statusFromCapacity(pct);
  const updated = venue.lastUpdated
    ? `Updated ${timeAgo(new Date(venue.lastUpdated as unknown as string))}`
    : "Updated just now";

  return (
    <div className="space-y-4" data-testid="live-tab-content">
      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-6" data-testid="live-capacity-card">
        <p className="text-6xl font-bold text-primary text-center" data-testid="text-live-capacity">{pct}%</p>
        <p className={`text-lg font-semibold text-center mt-2 ${status.textClass}`}>{status.label}</p>
        <div className="mt-6 mb-2">
          <div className="relative h-2 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-emerald-400 via-primary to-red-500"
              style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
            />
            <div
              className="absolute top-1/2 w-3 h-3 rounded-full bg-white border-2 border-zinc-900 -translate-y-1/2"
              style={{ left: `calc(${Math.max(0, Math.min(100, pct))}% - 6px)` }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-zinc-500 mt-1">
            <span>0</span>
            <span>25</span>
            <span>50</span>
            <span>75</span>
            <span>100%</span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-6">
          <span className="text-xs text-zinc-500 px-3 py-1 rounded-full border border-zinc-800">{updated}</span>
        </div>
      </Card>

      {analyticsLoading ? (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5">
          <div className="h-20 bg-zinc-800/50 rounded animate-pulse" />
        </Card>
      ) : analytics ? (
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="stat-peak-capacity">
            <p className="text-2xl font-bold text-primary">{analytics.reports.peakCapacity}%</p>
            <p className="text-[10px] text-zinc-500 mt-1">Peak capacity</p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="stat-avg-capacity">
            <p className="text-2xl font-bold text-white">{analytics.reports.avgCapacity}%</p>
            <p className="text-[10px] text-zinc-500 mt-1">Avg capacity</p>
          </Card>
          <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="stat-total-reports">
            <p className="text-2xl font-bold text-white">{analytics.reports.totalReports}</p>
            <p className="text-[10px] text-zinc-500 mt-1">Reports (30d)</p>
          </Card>
        </div>
      ) : null}
    </div>
  );
}

// ---------- Vibes Tab ----------
function VibesTab({ venue, analytics }: { venue: Venue; analytics: AnalyticsData | undefined }) {
  const { data: mediaResp } = useQuery<{ media: UserMediaItem[] }>({
    queryKey: ["/api/venues", venue.id, "media"],
    queryFn: async () => {
      const res = await fetch(`/api/venues/${venue.id}/media`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load media");
      return res.json();
    },
  });

  const items = mediaResp?.media ?? [];
  const totalVibes = analytics ? Object.values(analytics.reports.vibeBreakdown).reduce((a, b) => a + b, 0) : 0;
  const topTags = analytics
    ? Object.entries(analytics.reports.vibeBreakdown)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 6)
    : [];

  return (
    <div className="space-y-4" data-testid="vibes-tab-content">
      <div>
        <h2 className="text-2xl font-bold text-white">Vibes</h2>
        <p className="text-sm text-zinc-500 mt-0.5">What guests are posting about your venue</p>
      </div>

      {items.length === 0 ? (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-8 text-center" data-testid="vibes-empty">
          <p className="text-sm text-zinc-500">No guest posts yet for this venue.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map(item => (
            <Card
              key={item.id}
              className="bg-zinc-900 border-zinc-800 rounded-2xl p-3 flex gap-3"
              data-testid={`vibe-post-${item.id}`}
            >
              <div className="w-20 h-20 rounded-xl bg-zinc-800 overflow-hidden shrink-0 relative">
                {item.mediaType === "video" ? (
                  <>
                    {item.thumbnailUrl ? (
                      <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <PlayCircle className="w-7 h-7 text-white/90" />
                    </div>
                  </>
                ) : (
                  <img src={item.cloudinaryUrl} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-primary truncate">@{item.username}</p>
                  <span className="text-xs text-zinc-500">· {timeAgo(new Date(item.createdAt))}</span>
                </div>
                <p className="text-sm text-zinc-400 truncate">{venue.name}</p>
                <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1.5">
                  <span className="inline-flex items-center gap-1">
                    <ThumbsUp className="w-3 h-3" />
                    {item.thumbsUp}
                  </span>
                  {item.mediaType === "video" && (
                    <span className="px-2 py-0.5 rounded-full border border-blue-500/40 text-blue-400 text-[10px]">
                      Video
                    </span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {totalVibes > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="vibes-top-tags">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Top Tags From Guests</p>
          <div className="flex flex-wrap gap-2">
            {topTags.map(([tag, count]) => {
              const pct = Math.round((count / totalVibes) * 100);
              return (
                <span
                  key={tag}
                  data-testid={`vibe-tag-${tag.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-xs font-medium"
                >
                  {tag} · {pct}%
                </span>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------- Promote Tab ----------
function PromoteTab({
  venue,
  subTab,
  setSubTab,
  initialOpenForm,
  onConsumedInitial,
}: {
  venue: Venue;
  subTab: PromoteSubTab;
  setSubTab: (t: PromoteSubTab) => void;
  initialOpenForm: PromoteSubTab | null;
  onConsumedInitial: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: specials, isLoading: specialsLoading } = useBusinessSpecials();
  const [showCreateSpecial, setShowCreateSpecial] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newSpecial, setNewSpecial] = useState({ title: "", description: "", honeyCost: 0, active: true, expiresAt: "" });
  const [newEvent, setNewEvent] = useState({ title: "", dayOfWeek: "Monday", time: "", description: "", tag: "Drinks Deal" });

  useEffect(() => {
    if (initialOpenForm === "promotions") {
      setSubTab("promotions");
      setShowCreateSpecial(true);
      onConsumedInitial();
    } else if (initialOpenForm === "events") {
      setSubTab("events");
      setShowAddEvent(true);
      onConsumedInitial();
    }
  }, [initialOpenForm, setSubTab, onConsumedInitial]);

  const { data: venueEvents } = useQuery<VenueEvent[]>({
    queryKey: [`/api/venues/${venue.id}/events`],
    enabled: !!venue,
  });

  const { data: mediaResp } = useQuery<{ media: UserMediaItem[] }>({
    queryKey: ["/api/venues", venue.id, "media"],
    queryFn: async () => {
      const res = await fetch(`/api/venues/${venue.id}/media`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load media");
      return res.json();
    },
  });

  const createSpecial = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/business/specials", {
        ...newSpecial,
        honeyCost: Number(newSpecial.honeyCost),
        venueId: venue.id,
        expiresAt: newSpecial.expiresAt || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business/specials"] });
      queryClient.invalidateQueries({ queryKey: ["/api/venues", venue.id, "specials"] });
      setNewSpecial({ title: "", description: "", honeyCost: 0, active: true, expiresAt: "" });
      setShowCreateSpecial(false);
      toast({ title: "Promotion created" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const toggleSpecial = useMutation({
    mutationFn: async ({ id, active }: { id: number; active: boolean }) => {
      await apiRequest("PATCH", `/api/business/specials/${id}`, { active });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business/specials"] });
      queryClient.invalidateQueries({ queryKey: ["/api/venues", venue.id, "specials"] });
    },
  });

  const addEvent = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/venues/${venue.id}/events`, newEvent);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/venues/${venue.id}/events`] });
      setNewEvent({ title: "", dayOfWeek: "Monday", time: "", description: "", tag: "Drinks Deal" });
      setShowAddEvent(false);
      toast({ title: "Event added" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteEvent = useMutation({
    mutationFn: async (eventId: number) => {
      await apiRequest("DELETE", `/api/venues/${venue.id}/events/${eventId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/venues/${venue.id}/events`] });
      toast({ title: "Event removed" });
    },
  });

  const venueSpecials = (specials ?? []).filter((s: any) => s.venueId === venue.id);
  const activeSpecials = venueSpecials.filter((s: any) => s.active);
  const scheduledSpecials = venueSpecials.filter((s: any) => !s.active);

  return (
    <div className="space-y-4" data-testid="promote-tab-content">
      <div>
        <h2 className="text-2xl font-bold text-white">Promote</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Drive guests through the door</p>
      </div>

      <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-zinc-900 border border-zinc-800">
        <button
          data-testid="subtab-promotions"
          onClick={() => setSubTab("promotions")}
          className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            subTab === "promotions" ? "bg-zinc-800 text-white" : "text-zinc-500"
          }`}
        >
          Promotions
        </button>
        <button
          data-testid="subtab-events"
          onClick={() => setSubTab("events")}
          className={`py-2.5 rounded-xl text-sm font-semibold transition-colors ${
            subTab === "events" ? "bg-zinc-800 text-white" : "text-zinc-500"
          }`}
        >
          Events
        </button>
      </div>

      {subTab === "promotions" && (
        <div className="space-y-4">
          <Button
            data-testid="button-create-promotion"
            onClick={() => setShowCreateSpecial(v => !v)}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-2xl font-semibold"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showCreateSpecial ? "Cancel" : "Create Promotion"}
          </Button>

          {showCreateSpecial && (
            <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 space-y-3">
              <div>
                <Label className="text-xs text-zinc-500">Title</Label>
                <Input
                  data-testid="input-promo-title"
                  placeholder="e.g. Friday Happy Hour"
                  value={newSpecial.title}
                  onChange={e => setNewSpecial({ ...newSpecial, title: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Description</Label>
                <Textarea
                  data-testid="input-promo-description"
                  placeholder="Details about this offer..."
                  value={newSpecial.description}
                  onChange={e => setNewSpecial({ ...newSpecial, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white resize-none mt-1"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-zinc-500">Honey Cost</Label>
                  <Input
                    data-testid="input-promo-honey"
                    type="number"
                    min={0}
                    value={newSpecial.honeyCost}
                    onChange={e => setNewSpecial({ ...newSpecial, honeyCost: Number(e.target.value) })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Expires (optional)</Label>
                  <Input
                    data-testid="input-promo-expires"
                    type="datetime-local"
                    value={newSpecial.expiresAt}
                    onChange={e => setNewSpecial({ ...newSpecial, expiresAt: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              </div>
              <Button
                data-testid="button-save-promotion"
                onClick={() => createSpecial.mutate()}
                disabled={createSpecial.isPending || !newSpecial.title}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {createSpecial.isPending ? "Creating..." : "Save Promotion"}
              </Button>
            </Card>
          )}

          {specialsLoading && (
            <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5">
              <div className="h-20 bg-zinc-800/50 rounded animate-pulse" />
            </Card>
          )}

          {!specialsLoading && (
            <>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">Active</p>
                {activeSpecials.length === 0 ? (
                  <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5 text-center text-sm text-zinc-500" data-testid="active-promos-empty">
                    No active promotions.
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {activeSpecials.map((s: any) => (
                      <Card
                        key={s.id}
                        className="bg-zinc-900 border-zinc-800 rounded-2xl p-4"
                        data-testid={`promotion-active-${s.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-white">{s.title}</p>
                            {s.description && <p className="text-xs text-zinc-500 mt-0.5">{s.description}</p>}
                            <p className="text-xs text-primary mt-1">
                              {s.honeyCost === 0 ? "Free" : `⬡ ${s.honeyCost} Honey`}
                            </p>
                          </div>
                          <span className="text-[10px] px-2 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 inline-flex items-center gap-1 shrink-0">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            Live
                          </span>
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">Live</span>
                          <Switch
                            data-testid={`switch-promo-${s.id}`}
                            checked={s.active}
                            onCheckedChange={active => toggleSpecial.mutate({ id: s.id, active })}
                            disabled={toggleSpecial.isPending}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">Scheduled</p>
                {scheduledSpecials.length === 0 ? (
                  <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5 text-center text-sm text-zinc-500" data-testid="scheduled-promos-empty">
                    No scheduled promotions.
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {scheduledSpecials.map((s: any) => (
                      <Card
                        key={s.id}
                        className="bg-zinc-900 border-zinc-800 rounded-2xl p-4"
                        data-testid={`promotion-scheduled-${s.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-white">{s.title}</p>
                            {s.description && <p className="text-xs text-zinc-500 mt-0.5">{s.description}</p>}
                          </div>
                          <span className="text-[10px] px-2 py-1 rounded-full border border-zinc-700 text-zinc-400 shrink-0">
                            Scheduled
                          </span>
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-zinc-800">
                          <span className="text-xs text-zinc-500">Off</span>
                          <Switch
                            data-testid={`switch-promo-${s.id}`}
                            checked={s.active}
                            onCheckedChange={active => toggleSpecial.mutate({ id: s.id, active })}
                            disabled={toggleSpecial.isPending}
                          />
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {subTab === "events" && (
        <div className="space-y-4">
          <Button
            data-testid="button-add-event"
            onClick={() => setShowAddEvent(v => !v)}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-2xl font-semibold"
          >
            <Plus className="w-5 h-5 mr-2" />
            {showAddEvent ? "Cancel" : "Add Event"}
          </Button>

          {showAddEvent && (
            <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 space-y-3">
              <Input
                data-testid="input-event-title"
                placeholder="Event title"
                value={newEvent.title}
                onChange={e => setNewEvent({ ...newEvent, title: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <div className="grid grid-cols-2 gap-2">
                <Select value={newEvent.dayOfWeek} onValueChange={v => setNewEvent({ ...newEvent, dayOfWeek: v })}>
                  <SelectTrigger data-testid="select-event-day" className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAY_OPTIONS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="input-event-time"
                  placeholder="e.g. 7pm-11pm"
                  value={newEvent.time}
                  onChange={e => setNewEvent({ ...newEvent, time: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <Input
                data-testid="input-event-description"
                placeholder="Description"
                value={newEvent.description}
                onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
              <Select value={newEvent.tag} onValueChange={v => setNewEvent({ ...newEvent, tag: v })}>
                <SelectTrigger data-testid="select-event-tag" className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TAG_OPTIONS.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="button-save-event"
                onClick={() => addEvent.mutate()}
                disabled={!newEvent.title || !newEvent.time || addEvent.isPending}
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                {addEvent.isPending ? "Saving..." : "Save Event"}
              </Button>
            </Card>
          )}

          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">Upcoming</p>
            {(!venueEvents || venueEvents.length === 0) ? (
              <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5 text-center text-sm text-zinc-500" data-testid="events-empty">
                No upcoming events.
              </Card>
            ) : (
              <div className="space-y-3">
                {venueEvents.map(event => (
                  <Card
                    key={event.id}
                    className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 flex items-start gap-3"
                    data-testid={`event-row-${event.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white">{event.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {event.dayOfWeek} · {event.time}
                      </p>
                      {event.tag && (
                        <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full border border-zinc-700 text-zinc-400">
                          {event.tag}
                        </span>
                      )}
                    </div>
                    <Button
                      data-testid={`button-delete-event-${event.id}`}
                      variant="outline"
                      size="icon"
                      className="border-zinc-700 h-8 w-8 shrink-0"
                      onClick={() => deleteEvent.mutate(event.id)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {(mediaResp?.media?.length ?? 0) > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">Media</p>
              <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-3" data-testid="events-media-grid">
                <div className="grid grid-cols-3 gap-2">
                  {mediaResp!.media.slice(0, 9).map(m => (
                    <div
                      key={m.id}
                      className="aspect-square rounded-xl overflow-hidden bg-zinc-800 relative"
                      data-testid={`media-tile-${m.id}`}
                    >
                      {m.mediaType === "video" ? (
                        <>
                          {m.thumbnailUrl && <img src={m.thumbnailUrl} alt="" className="w-full h-full object-cover" />}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <PlayCircle className="w-6 h-6 text-white/90" />
                          </div>
                        </>
                      ) : (
                        <img src={m.cloudinaryUrl} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Branding upload helpers ----------
function useBrandingUpload(venueId: number, kind: "logo" | "cover") {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (file: Blob) => {
      const fd = new FormData();
      const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg";
      const filename = file instanceof File ? file.name : `${kind}.${ext}`;
      fd.append("file", file, filename);
      fd.append("kind", kind);
      const res = await fetch(`/api/business/venues/${venueId}/branding`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business/my-venues"] });
      toast({ title: kind === "logo" ? "Logo updated" : "Cover photo updated" });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Upload failed";
      toast({ title: "Upload failed", description: message, variant: "destructive" });
    },
  });
}

function useBrandingRemove(venueId: number, kind: "logo" | "cover") {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/business/venues/${venueId}/branding`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind }),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Remove failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business/my-venues"] });
      toast({ title: kind === "logo" ? "Logo removed" : "Cover photo removed" });
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Remove failed";
      toast({ title: "Remove failed", description: message, variant: "destructive" });
    },
  });
}

function BrandingCover({ venue }: { venue: Venue }) {
  const upload = useBrandingUpload(venue.id, "cover");
  const remove = useBrandingRemove(venue.id, "cover");
  const url = venue.coverPhotoUrl;
  const busy = upload.isPending || remove.isPending;
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  return (
    <div className="relative w-full">
      <label
        className="relative block w-full h-28 bg-zinc-800 cursor-pointer group overflow-hidden"
        data-testid="label-upload-cover"
      >
        {url ? (
          <img src={url} alt="Cover" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-500 text-xs">
            Add cover photo
          </div>
        )}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-medium">
          {upload.isPending ? "Uploading..." : remove.isPending ? "Removing..." : url ? "Change cover" : "Upload cover"}
        </div>
        <input
          data-testid="input-upload-cover"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={busy}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) setPendingFile(f);
            e.target.value = "";
          }}
        />
      </label>
      <BrandingCropDialog
        file={pendingFile}
        kind="cover"
        busy={upload.isPending}
        onCancel={() => setPendingFile(null)}
        onConfirm={blob => {
          upload.mutate(blob, { onSettled: () => setPendingFile(null) });
        }}
      />
      {url && (
        <button
          type="button"
          data-testid="button-remove-cover"
          disabled={busy}
          onClick={() => {
            if (confirm("Remove cover photo? The default Google cover will be shown instead.")) {
              remove.mutate();
            }
          }}
          className="absolute top-2 right-2 z-10 px-2 py-1 rounded-md bg-black/60 hover:bg-black/80 text-white text-xs font-medium disabled:opacity-50"
        >
          Remove
        </button>
      )}
    </div>
  );
}

function BrandingLogo({ venue }: { venue: Venue }) {
  const upload = useBrandingUpload(venue.id, "logo");
  const remove = useBrandingRemove(venue.id, "logo");
  const url = venue.logoUrl;
  const busy = upload.isPending || remove.isPending;
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  return (
    <div className="relative shrink-0">
      <label
        className="relative w-16 h-16 rounded-2xl bg-zinc-800 border-2 border-zinc-900 overflow-hidden cursor-pointer group flex items-center justify-center text-zinc-500 text-[10px]"
        data-testid="label-upload-logo"
      >
        {url ? (
          <img src={url} alt="Logo" className="w-full h-full object-cover" />
        ) : (
          <span>Logo</span>
        )}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-[10px] font-medium text-center px-1">
          {upload.isPending ? "..." : remove.isPending ? "..." : url ? "Change" : "Upload"}
        </div>
        <input
          data-testid="input-upload-logo"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={busy}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) setPendingFile(f);
            e.target.value = "";
          }}
        />
      </label>
      <BrandingCropDialog
        file={pendingFile}
        kind="logo"
        busy={upload.isPending}
        onCancel={() => setPendingFile(null)}
        onConfirm={blob => {
          upload.mutate(blob, { onSettled: () => setPendingFile(null) });
        }}
      />
      {url && (
        <button
          type="button"
          data-testid="button-remove-logo"
          disabled={busy}
          onClick={() => {
            if (confirm("Remove logo?")) {
              remove.mutate();
            }
          }}
          className="absolute -top-1 -right-1 z-10 w-5 h-5 rounded-full bg-black/80 hover:bg-black text-white text-[10px] leading-none flex items-center justify-center disabled:opacity-50"
          aria-label="Remove logo"
        >
          ×
        </button>
      )}
    </div>
  );
}

// ---------- Profile Tab ----------
function ProfileTab({ venue, onSignOut }: { venue: Venue | null; onSignOut: () => void }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: venue?.name ?? "",
    description: venue?.description ?? "",
    type: venue?.type ?? "bar",
    capacityLevel: String(venue?.capacityLevel ?? 0),
    instagramUrl: venue?.instagramUrl ?? "",
    tiktokUrl: venue?.tiktokUrl ?? "",
    websiteUrl: venue?.websiteUrl ?? "",
  });

  useEffect(() => {
    if (!venue) return;
    setForm({
      name: venue.name,
      description: venue.description ?? "",
      type: venue.type,
      capacityLevel: String(venue.capacityLevel ?? 0),
      instagramUrl: venue.instagramUrl ?? "",
      tiktokUrl: venue.tiktokUrl ?? "",
      websiteUrl: venue.websiteUrl ?? "",
    });
  }, [venue?.id]);

  const update = useMutation({
    mutationFn: async () => {
      if (!venue) return;
      await apiRequest("PUT", `/api/business/venues/${venue.id}`, {
        name: form.name,
        description: form.description,
        type: form.type,
        capacityLevel: parseInt(form.capacityLevel) || 0,
        instagramUrl: form.instagramUrl || null,
        tiktokUrl: form.tiktokUrl || null,
        websiteUrl: form.websiteUrl || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/business/my-venues"] });
      toast({ title: "Venue updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  if (!venue) {
    return (
      <div className="space-y-4" data-testid="profile-tab-content">
        <div>
          <h2 className="text-2xl font-bold text-white">Profile</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Manage your venue on Buzzn</p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-6 text-center" data-testid="profile-no-venue">
          <p className="text-base font-medium text-white">No venues yet</p>
          <p className="text-sm text-zinc-500 mt-1">Ask an admin to assign venues to your account.</p>
        </Card>

        <Button
          data-testid="button-sign-out"
          variant="outline"
          onClick={onSignOut}
          className="w-full h-12 rounded-2xl border-zinc-800 bg-zinc-900 text-white hover:bg-zinc-800"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    );
  }

  const addressLine = `${venue.latitude.toFixed(4)}, ${venue.longitude.toFixed(4)}`;

  return (
    <div className="space-y-4" data-testid="profile-tab-content">
      <div>
        <h2 className="text-2xl font-bold text-white">Profile</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Manage your venue on Buzzn</p>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl overflow-hidden" data-testid="profile-header-card">
        <BrandingCover venue={venue} />
        <div className="p-4 flex items-center gap-3 -mt-8 relative z-10">
          <BrandingLogo venue={venue} />
          <div className="flex-1 min-w-0 pt-8">
            <p className="text-lg font-bold text-white break-words leading-tight" data-testid="text-profile-venue-name">{venue.name}</p>
            <p className="text-xs text-zinc-500 capitalize" data-testid="text-profile-venue-meta">
              {venue.type} · {addressLine}
            </p>
          </div>
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5 space-y-4" data-testid="profile-details-card">
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Details</p>
        <div>
          <Label className="text-xs text-zinc-500">Venue name</Label>
          <Input
            data-testid="input-profile-name"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Description</Label>
          <Textarea
            data-testid="input-profile-description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white mt-1 resize-none"
            rows={3}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-500">Category</Label>
            <Select value={form.type} onValueChange={v => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="select-profile-type" className="bg-zinc-800 border-zinc-700 text-white mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bar">Bar</SelectItem>
                <SelectItem value="pub">Pub</SelectItem>
                <SelectItem value="club">Club</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Capacity Level</Label>
            <Input
              data-testid="input-profile-capacity"
              type="number"
              min={0}
              max={100}
              value={form.capacityLevel}
              onChange={e => setForm({ ...form, capacityLevel: e.target.value })}
              className="bg-zinc-800 border-zinc-700 text-white mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Instagram URL</Label>
          <Input
            data-testid="input-profile-instagram"
            placeholder="https://instagram.com/yourvenue"
            value={form.instagramUrl}
            onChange={e => setForm({ ...form, instagramUrl: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">TikTok URL</Label>
          <Input
            data-testid="input-profile-tiktok"
            placeholder="https://tiktok.com/@yourvenue"
            value={form.tiktokUrl}
            onChange={e => setForm({ ...form, tiktokUrl: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Website URL</Label>
          <Input
            data-testid="input-profile-website"
            placeholder="https://yourvenue.com"
            value={form.websiteUrl}
            onChange={e => setForm({ ...form, websiteUrl: e.target.value })}
            className="bg-zinc-800 border-zinc-700 text-white mt-1"
          />
        </div>
        <Button
          data-testid="button-save-profile"
          onClick={() => update.mutate()}
          disabled={update.isPending}
          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          {update.isPending ? "Saving..." : "Save Changes"}
        </Button>
      </Card>

      <Button
        data-testid="button-sign-out"
        variant="outline"
        onClick={onSignOut}
        className="w-full h-12 rounded-2xl border-zinc-800 bg-zinc-900 text-white hover:bg-zinc-800"
      >
        <LogOut className="w-4 h-4 mr-2" />
        Sign Out
      </Button>
    </div>
  );
}

// ---------- All Venues Overview ----------
type CapacityHistoryResp = {
  dates: string[];
  venues: { venueId: number; venueName: string; data: number[] }[];
};

const VENUE_LINE_COLORS = [
  "#FED100",
  "#38bdf8",
  "#34d399",
  "#f472b6",
  "#fb923c",
  "#a78bfa",
  "#22d3ee",
  "#f87171",
  "#facc15",
  "#4ade80",
];

function AllVenuesOverview({ venues, onSelect }: { venues: Venue[]; onSelect: (v: Venue) => void }) {
  const [historyRange, setHistoryRange] = useState<CapacityHistoryRange>(7);
  const { data: history, isLoading: historyLoading } = useQuery<CapacityHistoryResp>({
    queryKey: ["/api/business/analytics/capacity-history", { range: historyRange }],
    queryFn: async () => {
      const res = await fetch(`/api/business/analytics/capacity-history?range=${historyRange}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load capacity history");
      return res.json();
    },
    enabled: venues.length > 0,
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (venues.length === 0) {
    return (
      <div className="px-4 py-12 text-center text-zinc-500" data-testid="all-venues-empty">
        <p className="text-base font-medium">No venues yet</p>
        <p className="text-sm mt-1">Ask an admin to assign venues to your account.</p>
      </div>
    );
  }

  const totalReports = venues.reduce((s, v) => s + (v.reportCount ?? 0), 0);
  const avgCapacity = Math.round(
    venues.reduce((s, v) => s + (v.capacityLevel ?? 0), 0) / venues.length,
  );

  // Avg Google rating across venues that have a rating.
  const ratedVenues = venues.filter(v => typeof v.googleRating === "number" && v.googleRating! > 0);
  const avgRating = ratedVenues.length
    ? ratedVenues.reduce((s, v) => s + (v.googleRating ?? 0), 0) / ratedVenues.length
    : null;

  // Status distribution (Quiet / Lively / Buzzing / Packed).
  const buckets = { Empty: 0, Quiet: 0, Steady: 0, Busy: 0, "Buzzin'": 0 } as Record<string, number>;
  for (const v of venues) buckets[statusFromCapacity(v.capacityLevel ?? 0).label]++;
  const segments = [
    { key: "Empty",   count: buckets.Empty,        bar: "bg-blue-400",    text: "text-blue-400" },
    { key: "Quiet",   count: buckets.Quiet,        bar: "bg-emerald-400", text: "text-emerald-400" },
    { key: "Steady",  count: buckets.Steady,       bar: "bg-primary",     text: "text-primary" },
    { key: "Busy",    count: buckets.Busy,         bar: "bg-orange-400",  text: "text-orange-400" },
    { key: "Buzzin'", count: buckets["Buzzin'"],   bar: "bg-red-500",     text: "text-red-500" },
  ];

  // Sorted for the list; also gives us Top 3 busiest.
  const sortedByCapacity = venues
    .slice()
    .sort((a, b) => (b.capacityLevel ?? 0) - (a.capacityLevel ?? 0));
  const topBusy = sortedByCapacity.slice(0, 3);

  // Per-venue capacity history: one line per venue.
  const trendVenues = history?.venues ?? [];
  const trendChartData = (() => {
    if (!history || history.venues.length === 0) return [] as Record<string, string | number>[];
    return history.dates.map((day, i) => {
      const row: Record<string, string | number> = { day };
      for (const v of history.venues) {
        row[v.venueName] = v.data[i] ?? 0;
      }
      return row;
    });
  })();
  const trendHasData = trendVenues.some(v => v.data.some(d => d > 0));

  return (
    <div className="space-y-4" data-testid="all-venues-overview">
      <div>
        <h2 className="text-2xl font-bold text-white">All Venues</h2>
        <p className="text-sm text-zinc-500 mt-0.5">Snapshot across your portfolio</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="overview-stat-venues">
          <p className="text-2xl font-bold text-primary">{venues.length}</p>
          <p className="text-[10px] text-zinc-500 mt-1">Venues</p>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="overview-stat-avg-capacity">
          <p className="text-2xl font-bold text-white">{avgCapacity}%</p>
          <p className="text-[10px] text-zinc-500 mt-1">Avg capacity</p>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-4 text-center" data-testid="overview-stat-avg-rating">
          <p className="text-2xl font-bold text-white">
            {avgRating !== null ? avgRating.toFixed(1) : "—"}
            {avgRating !== null && <span className="text-base text-zinc-500"> ★</span>}
          </p>
          <p className="text-[10px] text-zinc-500 mt-1">Avg Google rating</p>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="overview-status-breakdown">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Right Now</p>
          <span className="text-[10px] text-zinc-600">{venues.length} venues</span>
        </div>
        <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-zinc-800">
          {segments.map(s =>
            s.count > 0 ? (
              <div
                key={s.key}
                className={s.bar}
                style={{ width: `${(s.count / venues.length) * 100}%` }}
                title={`${s.key}: ${s.count}`}
              />
            ) : null,
          )}
        </div>
        <div className="grid grid-cols-4 gap-2 mt-3">
          {segments.map(s => (
            <div key={s.key} className="text-center" data-testid={`status-bucket-${s.key.toLowerCase()}`}>
              <p className={`text-lg font-bold ${s.text}`}>{s.count}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{s.key}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="overview-portfolio-trend">
        <div className="flex items-center justify-between mb-3 gap-2">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Capacity History</p>
          <div
            className="inline-flex items-center bg-zinc-800/70 rounded-full p-0.5"
            role="tablist"
            aria-label="Capacity history range"
            data-testid="overview-capacity-history-range-toggle"
          >
            {CAPACITY_HISTORY_RANGE_OPTIONS.map(opt => {
              const active = opt.value === historyRange;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setHistoryRange(opt.value)}
                  data-testid={`button-overview-capacity-history-range-${opt.value}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${active ? "bg-primary text-primary-foreground" : "text-zinc-400 hover:text-white"}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
        {historyLoading ? (
          <div className="h-48 bg-zinc-800/50 rounded animate-pulse" />
        ) : trendHasData ? (
          <div className="h-48" data-testid="overview-capacity-history-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendChartData} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fill: "#71717a", fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: "#27272a" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fill: "#71717a", fontSize: 10 }}
                  tickLine={false}
                  axisLine={{ stroke: "#27272a" }}
                  tickFormatter={(v) => `${v}%`}
                  width={36}
                />
                <Tooltip
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(value: number, name: string) => [`${value}%`, name]}
                />
                {trendVenues.map((v, idx) => {
                  const color = VENUE_LINE_COLORS[idx % VENUE_LINE_COLORS.length];
                  return (
                    <Line
                      key={v.venueId}
                      type="monotone"
                      dataKey={v.venueName}
                      stroke={color}
                      strokeWidth={2}
                      dot={{ r: 2, fill: color }}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : null}
        {!historyLoading && trendHasData && (
          <div
            className="mt-3 max-h-16 overflow-y-auto pr-1 flex flex-wrap gap-x-3 gap-y-1"
            data-testid="overview-capacity-history-legend"
          >
            {trendVenues.map((v, idx) => {
              const color = VENUE_LINE_COLORS[idx % VENUE_LINE_COLORS.length];
              return (
                <span
                  key={v.venueId}
                  className="inline-flex items-center gap-1 text-[10px] text-zinc-400 min-w-0"
                  title={v.venueName}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: color }}
                  />
                  <span className="truncate max-w-[7rem]">{v.venueName}</span>
                </span>
              );
            })}
          </div>
        )}
        {!historyLoading && !trendHasData && (
          <p className="text-xs text-zinc-500 text-center py-8" data-testid="overview-capacity-history-empty">
            No history available yet
          </p>
        )}
      </Card>

      {topBusy.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 rounded-2xl p-5" data-testid="overview-top-busy">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">
            Busiest Right Now
          </p>
          <div className="space-y-2">
            {topBusy.map((v, idx) => {
              const status = statusFromCapacity(v.capacityLevel ?? 0);
              return (
                <button
                  key={v.id}
                  onClick={() => onSelect(v)}
                  data-testid={`top-busy-${v.id}`}
                  className="w-full flex items-center gap-3 p-2 -mx-2 rounded-xl hover-elevate active-elevate-2 text-left"
                >
                  <span className="w-6 text-center text-sm font-semibold text-zinc-500">{idx + 1}</span>
                  <span className="flex-1 min-w-0 truncate text-sm font-medium text-white">{v.name}</span>
                  <span className={`text-sm font-bold ${status.textClass}`}>{v.capacityLevel ?? 0}%</span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      <div>
        <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2 px-1">
          All Venues · {totalReports.toLocaleString()} reports total
        </p>
        <div className="space-y-2">
          {sortedByCapacity.map(v => {
            const status = statusFromCapacity(v.capacityLevel ?? 0);
            const updated = v.lastUpdated
              ? timeAgo(new Date(v.lastUpdated as unknown as string))
              : null;
            return (
              <button
                key={v.id}
                data-testid={`overview-venue-${v.id}`}
                onClick={() => onSelect(v)}
                className="w-full text-left bg-zinc-900 border border-zinc-800 rounded-2xl p-4 flex items-center gap-3 hover-elevate active-elevate-2"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white truncate">{v.name}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-zinc-500 capitalize">{v.type}</span>
                    <span className="text-zinc-700">·</span>
                    <span className={`inline-flex items-center gap-1 text-xs ${status.textClass}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dotClass}`} />
                      {status.label}
                    </span>
                    <span className="text-zinc-700">·</span>
                    <span className="text-xs text-zinc-500">
                      {(v.reportCount ?? 0).toLocaleString()} reports
                    </span>
                    {updated && (
                      <>
                        <span className="text-zinc-700">·</span>
                        <span className="text-xs text-zinc-500">{updated}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-bold text-white">{v.capacityLevel ?? 0}%</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Bottom Nav ----------
function BottomNav({
  active,
  onChange,
}: {
  active: TabId | null;
  onChange: (tab: TabId) => void;
}) {
  const items: { id: TabId; label: string; Icon: typeof HomeIcon }[] = [
    { id: "home", label: "Home", Icon: HomeIcon },
    { id: "live", label: "Live", Icon: Radio },
    { id: "vibes", label: "Vibes", Icon: MessageSquare },
    { id: "promote", label: "Promote", Icon: Star },
    { id: "profile", label: "Profile", Icon: UserIcon },
  ];

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 pointer-events-none"
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.5rem)" }}
    >
      <div className="max-w-md mx-auto px-4">
        <nav
          data-testid="business-bottom-nav"
          className="pointer-events-auto bg-zinc-900/95 backdrop-blur border border-zinc-800 rounded-3xl shadow-lg shadow-black/40 px-2 py-2 flex items-center justify-between"
        >
          {items.map(({ id, label, Icon }) => {
            const isActive = active === id;
            return (
              <button
                key={id}
                data-testid={`bnav-${id}`}
                onClick={() => onChange(id)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-2xl transition-colors ${
                  isActive ? "text-primary" : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? "text-primary" : ""}`} />
                <span className="text-[10px] font-medium">{label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}

// ---------- Main Page ----------
export default function BusinessPanel() {
  const { user, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const search = useSearch();
  const logout = useLogout();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [promoteSubTab, setPromoteSubTab] = useState<PromoteSubTab>("promotions");
  const [pendingPromoteForm, setPendingPromoteForm] = useState<PromoteSubTab | null>(null);

  const params = new URLSearchParams(search);
  const venueIdParam = params.get("venue");
  const tabParam = params.get("tab") as TabId | null;
  const selectedVenueId = useMemo(() => {
    if (!venueIdParam) return null;
    const n = Number(venueIdParam);
    return Number.isFinite(n) ? n : null;
  }, [venueIdParam]);

  const validTabs: TabId[] = ["home", "live", "vibes", "promote", "profile"];
  const activeTab: TabId = tabParam && validTabs.includes(tabParam) ? tabParam : "home";

  type VenueWithCapacitySource = Venue & { source?: CapacitySource | null };

  const { data: myVenues } = useQuery<VenueWithCapacitySource[]>({
    queryKey: ["/api/business/my-venues"],
    enabled: !!user && (user.role === "business" || user.role === "admin"),
    staleTime: 0,
    refetchOnMount: "always",
  });

  const selectedVenue = useMemo<VenueWithCapacitySource | null>(
    () => (selectedVenueId && myVenues ? myVenues.find(v => v.id === selectedVenueId) ?? null : null),
    [selectedVenueId, myVenues],
  );

  const [checkInsRange, setCheckInsRange] = useState<CheckInsRange>(7);

  const { data: analytics, isLoading: analyticsLoading } = useQuery<AnalyticsData>({
    queryKey: ["/api/business/venues", selectedVenue?.id, "analytics", { range: checkInsRange }],
    queryFn: async () => {
      const res = await fetch(
        `/api/business/venues/${selectedVenue!.id}/analytics?range=${checkInsRange}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error("Failed to load analytics");
      return res.json();
    },
    enabled: !!selectedVenue,
    staleTime: 0,
    refetchOnMount: "always",
  });

  function setTab(tab: TabId) {
    const p = new URLSearchParams(search);
    p.set("tab", tab);
    if (selectedVenueId) p.set("venue", String(selectedVenueId));
    setLocation(`/business?${p.toString()}`);
  }

  function selectVenue(v: Venue, opts?: { tab?: TabId }) {
    const p = new URLSearchParams();
    p.set("venue", String(v.id));
    p.set("tab", opts?.tab ?? "home");
    setLocation(`/business?${p.toString()}`);
    setSwitcherOpen(false);
  }

  function selectAllVenues() {
    setLocation("/business");
    setSwitcherOpen(false);
  }

  function handleQuickAction(kind: "promo" | "event" | "profile") {
    if (kind === "promo") {
      setPromoteSubTab("promotions");
      setPendingPromoteForm("promotions");
      setTab("promote");
    } else if (kind === "event") {
      setPromoteSubTab("events");
      setPendingPromoteForm("events");
      setTab("promote");
    } else {
      setTab("profile");
    }
  }

  function handleSignOut() {
    logout.mutateAsync().then(() => setLocation("/auth"));
  }

  if (authLoading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-white">Loading...</div>;
  }
  if (!user || (user.role !== "business" && user.role !== "admin")) {
    setLocation("/auth");
    return null;
  }

  const status = selectedVenue ? statusFromCapacity(selectedVenue.capacityLevel ?? 0) : null;
  const headerLabel = selectedVenue ? selectedVenue.name : "All Venues";

  return (
    <div className="h-screen overflow-hidden bg-background text-white flex flex-col">
      <div className="max-w-md w-full mx-auto flex-1 overflow-y-auto pb-32" data-testid="business-panel-v2">
        <header
          className="px-4 pt-[max(env(safe-area-inset-top),1rem)] pb-3"
          data-testid="business-header"
        >
          <p className="text-xs text-zinc-500" data-testid="text-greeting">{greeting()}</p>
          <Sheet open={switcherOpen} onOpenChange={setSwitcherOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="button-venue-switcher"
                className="flex items-start gap-2 mt-1 group w-full"
              >
                <h1 className="text-2xl font-bold text-white text-left break-words leading-tight flex-1 min-w-0">{headerLabel}</h1>
                <ChevronDown className="w-5 h-5 text-zinc-500 group-hover:text-white transition-colors shrink-0 mt-1" />
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-zinc-950 border-zinc-800 max-h-[80vh] rounded-t-3xl">
              <SheetHeader>
                <SheetTitle className="text-white">Switch Venue</SheetTitle>
                <SheetDescription className="sr-only">
                  Pick which venue or your full portfolio to view in the business panel
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-2 overflow-y-auto pb-6">
                <button
                  data-testid="switcher-all-venues"
                  onClick={selectAllVenues}
                  className={`w-full text-left p-4 rounded-2xl border ${
                    !selectedVenue ? "border-primary/40 bg-primary/10" : "border-zinc-800 bg-zinc-900"
                  } hover-elevate active-elevate-2`}
                >
                  <p className={`font-semibold ${!selectedVenue ? "text-primary" : "text-white"}`}>All Venues</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Portfolio overview</p>
                </button>
                {(myVenues ?? []).map(v => (
                  <button
                    key={v.id}
                    data-testid={`switcher-venue-${v.id}`}
                    onClick={() => selectVenue(v)}
                    className={`w-full text-left p-4 rounded-2xl border ${
                      selectedVenue?.id === v.id ? "border-primary/40 bg-primary/10" : "border-zinc-800 bg-zinc-900"
                    } hover-elevate active-elevate-2`}
                  >
                    <p className={`font-semibold ${selectedVenue?.id === v.id ? "text-primary" : "text-white"}`}>
                      {v.name}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5 capitalize">
                      {v.type} · {v.capacityLevel ?? 0}%
                    </p>
                  </button>
                ))}
              </div>
            </SheetContent>
          </Sheet>
          {selectedVenue && status && (
            <div className="flex items-center gap-2 mt-2" data-testid="venue-status-pill">
              <span className={`inline-flex items-center gap-1.5 text-xs ${status.textClass}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${status.dotClass}`} />
                {status.label}
              </span>
              <span className="text-zinc-700">·</span>
              <span className="text-xs text-zinc-500">{selectedVenue.capacityLevel ?? 0}% full</span>
              {selectedVenue.source === "claude_estimate" && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-xs text-zinc-500">Weekly Claude estimate</span>
                </>
              )}
              {selectedVenue.source === "serpapi_histogram" && (
                <>
                  <span className="text-zinc-700">·</span>
                  <span className="text-xs text-zinc-500">SerpAPI popular-times</span>
                </>
              )}
            </div>
          )}
        </header>

        <main className="px-4 mt-3">
          {activeTab === "profile" ? null : !selectedVenue ? (
            <AllVenuesOverview venues={myVenues ?? []} onSelect={(v) => selectVenue(v)} />
          ) : (
            <>
              {activeTab === "home" && (
                <HomeTab
                  venue={selectedVenue}
                  analytics={analytics}
                  analyticsLoading={analyticsLoading}
                  checkInsRange={checkInsRange}
                  onCheckInsRangeChange={setCheckInsRange}
                  onQuickAction={handleQuickAction}
                  onViewLive={() => setTab("live")}
                />
              )}
              {activeTab === "live" && (
                <LiveTab venue={selectedVenue} analytics={analytics} analyticsLoading={analyticsLoading} />
              )}
              {activeTab === "vibes" && <VibesTab venue={selectedVenue} analytics={analytics} />}
              {activeTab === "promote" && (
                <PromoteTab
                  venue={selectedVenue}
                  subTab={promoteSubTab}
                  setSubTab={setPromoteSubTab}
                  initialOpenForm={pendingPromoteForm}
                  onConsumedInitial={() => setPendingPromoteForm(null)}
                />
              )}
            </>
          )}
          {activeTab === "profile" && <ProfileTab venue={selectedVenue} onSignOut={handleSignOut} />}
        </main>
      </div>

      <BottomNav
        active={selectedVenue ? activeTab : null}
        onChange={(tab) => {
          if (selectedVenue) {
            setTab(tab);
          } else if ((myVenues ?? []).length > 0) {
            selectVenue(myVenues![0], { tab });
          } else if (tab === "profile") {
            setTab("profile");
          } else {
            setSwitcherOpen(true);
          }
        }}
      />
    </div>
  );
}
